import { useState, useEffect } from "react";
import { Search, Image as ImageIcon, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { findTable } from "@/lib/sizing-store";

/**
 * LinkProductsModal — full-canvas dedicated experience for the bulk product
 * linking flow. Lives in its own Dialog rather than inside the Edit drawer so
 * merchants working through 1000-2000 SKU catalogs can breathe: bigger search,
 * bigger cards with thumbnails, sticky sub-header with select-all + counters,
 * sticky footer with commit CTA.
 *
 * Owns its own local state (query, selection, move-confirm). Emits a single
 * `onLinked({ linked, moved })` callback after `linkProduct` fires for each
 * picked ID so the parent can toast + close.
 */
export const LinkProductsModal = ({ open, onClose, table, products, links, tables, linkProduct, onLinked, onCommit, allowCommitWithoutMove }) => {
    const [query, setQuery] = useState("");
    const [picked, setPicked] = useState(new Set());
    const [confirmMove, setConfirmMove] = useState(false);
    // Reset selection when modal opens/closes so we don't leak state between
    // sequential "Ürün ekle" clicks on different tables.
    useEffect(() => {
        if (!open) { setQuery(""); setPicked(new Set()); setConfirmMove(false); }
    }, [open]);

    if (!table) return null;

    const q = query.trim().toLowerCase();
    const excludeId = table.id || null;
    const results = q.length >= 2
        ? products
            .filter((p) => (excludeId ? links[p.id] !== excludeId : true))
            .filter((p) => (p.name || "").toLowerCase().includes(q) || (p.id || "").toLowerCase().includes(q))
            .slice(0, 200)
        : [];

    const toggle = (pid) => setPicked((prev) => {
        const n = new Set(prev);
        if (n.has(pid)) n.delete(pid); else n.add(pid);
        return n;
    });
    const allInResults = results.length > 0 && results.every((p) => picked.has(p.id));
    const toggleAll = () => setPicked((prev) => {
        const n = new Set(prev);
        if (allInResults) results.forEach((p) => n.delete(p.id));
        else results.forEach((p) => n.add(p.id));
        return n;
    });
    const pickedList = Array.from(picked).map((pid) => products.find((p) => p.id === pid)).filter(Boolean);
    const moveCount = pickedList.filter((p) => links[p.id] && links[p.id] !== excludeId).length;

    const commit = () => {
        const ids = Array.from(picked);
        // Wizard flow — onCommit takes over (table doesn't exist yet). Regular
        // flow — link each picked id directly + fire onLinked toast callback.
        if (onCommit) {
            onCommit(ids);
        } else {
            ids.forEach((pid) => linkProduct(pid, table.id));
            if (onLinked) onLinked({ linked: ids.length, moved: moveCount });
        }
        setPicked(new Set());
        setQuery("");
        setConfirmMove(false);
    };
    // If we're in wizard mode (allowCommitWithoutMove) there's nothing to
    // reassign yet — skip the "başka tablodan taşınacak" alert dialog.
    const onPrimary = () => (moveCount > 0 && !allowCommitWithoutMove ? setConfirmMove(true) : commit());

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
                <DialogContent
                    className="max-w-[900px] w-[92vw] p-0 gap-0 overflow-hidden flex flex-col"
                    style={{ height: "min(88vh, 780px)" }}
                    data-testid="link-products-modal"
                >
                    <DialogTitle className="sr-only">Ürün Bağla</DialogTitle>
                    <DialogDescription className="sr-only">
                        {table.name} tablosuna ürün ekle. Ürün adı veya kodu ile arayabilir, toplu seçim yapabilirsin.
                    </DialogDescription>

                    {/* Header */}
                    <div className="px-6 py-5 border-b border-border shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                            Ürünleri bağla
                        </div>
                        <h2 className="text-lg font-display font-bold tracking-tight leading-tight">
                            {table.name}
                        </h2>
                        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                            Ürün adı veya ürün kodu ile ara, checkbox ile toplu seç. Bir ürün yalnızca bir tabloda olabilir — başka tablodakiler seçilirse bu tabloya <span className="font-medium text-foreground">taşınır</span>.
                        </p>
                    </div>

                    {/* Sticky search bar */}
                    <div className="px-6 py-4 border-b border-border bg-muted/20 shrink-0">
                        <div className="relative">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Ürün adı veya kodu yaz… (en az 2 karakter)"
                                className="h-11 pl-10 text-xs bg-background"
                                data-testid="link-modal-search"
                            />
                        </div>
                    </div>

                    {/* Results grid — scrollable body */}
                    <div className="flex-1 overflow-y-auto scroll-thin px-6 py-6">
                        {q.length < 2 ? (
                            <div className="h-full min-h-[280px] grid place-items-center text-center" data-testid="link-modal-hint">
                                <div className="max-w-sm">
                                    <div className="h-14 w-14 mx-auto rounded-full bg-muted grid place-items-center mb-3">
                                        <Search className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                                    </div>
                                    <div className="text-sm font-medium mb-1">Aramaya başla</div>
                                    <p className="text-xs text-muted-foreground">
                                        Ürün adı veya kodu ile katalogda ara. En az 2 karakter yazınca sonuçlar burada listelenir.
                                    </p>
                                </div>
                            </div>
                        ) : results.length === 0 ? (
                            <div className="h-full min-h-[280px] grid place-items-center text-center" data-testid="link-modal-empty">
                                <div>
                                    <div className="text-sm font-medium mb-1">Sonuç yok</div>
                                    <p className="text-xs text-muted-foreground">"{query.trim()}" ile eşleşen bir ürün bulunamadı.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-border overflow-hidden" data-testid="link-modal-results">
                                <table className="w-full table-fixed">
                                    <thead>
                                        <tr className="border-b border-border bg-muted/30">
                                            <th className="w-[44px] pl-4 pr-0 py-2.5 align-middle">
                                                <div className="flex items-center">
                                                    <Checkbox
                                                        checked={allInResults}
                                                        onCheckedChange={toggleAll}
                                                        data-testid="link-modal-select-all"
                                                    />
                                                </div>
                                            </th>
                                            <th className="w-[68px] text-left text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground px-3 py-2.5">Görsel</th>
                                            <th className="w-[160px] text-left text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground px-3 py-2.5">Ürün Kodu</th>
                                            <th className="text-left text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground px-3 py-2.5">Ürün Adı</th>
                                            <th className="w-[170px] text-right text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground px-4 py-2.5">Tablo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((p) => {
                                            const other = links[p.id] ? findTable(tables, links[p.id]) : null;
                                            const checked = picked.has(p.id);
                                            return (
                                                <tr
                                                    key={p.id}
                                                    onClick={() => toggle(p.id)}
                                                    className={`border-b border-border last:border-0 cursor-pointer transition-colors ${checked ? "bg-muted/40" : "hover:bg-muted/20"}`}
                                                    data-testid={`link-modal-item-${p.id}`}
                                                >
                                                    <td className="pl-4 pr-0 py-2 align-middle">
                                                        <div className="flex items-center">
                                                            <Checkbox
                                                                checked={checked}
                                                                onCheckedChange={() => toggle(p.id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                data-testid={`link-modal-item-check-${p.id}`}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 align-middle">
                                                        <div className="h-9 w-9 rounded-md border border-border bg-muted overflow-hidden">
                                                            {p.image ? (
                                                                <img src={p.image} alt="" className="h-full w-full object-cover" />
                                                            ) : (
                                                                <div className="h-full w-full grid place-items-center">
                                                                    <ImageIcon className="h-3 w-3 text-muted-foreground/40" strokeWidth={1.5} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 align-middle">
                                                        <span className="text-[11px] font-mono text-muted-foreground truncate block">{p.id}</span>
                                                    </td>
                                                    <td className="px-3 py-2 align-middle">
                                                        <span className="text-xs font-medium truncate block">
                                                            {p.name || <span className="text-muted-foreground italic">İsim yok</span>}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 align-middle text-right">
                                                        {other ? (
                                                            <Badge variant="outline" className="text-[9.5px] capitalize border-warning/40 text-warning bg-warning-soft">
                                                                şu an: {other.name}
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="text-[9.5px] capitalize text-muted-foreground">tablosuz</Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Sticky footer */}
                    <div className="px-6 py-4 border-t border-border bg-background flex items-center justify-between gap-3 shrink-0">
                        <div className="text-xs text-muted-foreground min-w-0">
                            {picked.size === 0 ? (
                                <span>Henüz ürün seçmedin.</span>
                            ) : (
                                <span>
                                    <span className="font-medium text-foreground">{picked.size}</span> ürün seçili
                                    {moveCount > 0 && <> · <span className="text-warning">{moveCount} tanesi başka tablodan taşınacak</span></>}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="h-9 text-xs"
                                data-testid="link-modal-cancel"
                            >
                                İptal
                            </Button>
                            <Button
                                disabled={picked.size === 0}
                                onClick={onPrimary}
                                className="h-9 text-xs gap-2 bg-brand hover:bg-brand/90 text-brand-fg disabled:opacity-50 disabled:pointer-events-none"
                                data-testid="link-modal-commit"
                            >
                                <Link2 className="h-3.5 w-3.5" /> Tabloya Bağla{picked.size > 0 && ` (${picked.size})`}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Move confirmation for the selected items whose links belong to
                other tables. Only shown when moveCount > 0. */}
            <AlertDialog open={confirmMove} onOpenChange={(o) => !o && setConfirmMove(false)}>
                <AlertDialogContent data-testid="bulk-link-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{picked.size} ürünü bu tabloya bağla</AlertDialogTitle>
                        <AlertDialogDescription>
                            Seçtiğin <span className="font-medium text-foreground">{picked.size} üründen {moveCount} tanesi</span> şu an başka bir tabloda. Onaylarsan bağlantıları oradan alınıp <span className="font-medium text-foreground">&quot;{table.name}&quot;</span> tablosuna taşınır. Kalan {picked.size - moveCount} ürün doğrudan bağlanır.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bulk-link-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction onClick={commit} data-testid="bulk-link-confirm">
                            Evet, hepsini bağla
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
